"use client";

import { useState, useEffect, ReactNode } from "react";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/lib/use-translations";
import api from "@/lib/api/client";

const AUTH_STORAGE_KEY = "saysay-auth";

interface PasswordGateProps {
  children: ReactNode;
}

export function PasswordGate({ children }: PasswordGateProps) {
  const t = useTranslations();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      // Check for password in URL query string
      const urlParams = new URLSearchParams(window.location.search);
      const queryPassword = urlParams.get("p");

      if (queryPassword) {
        // Try to authenticate with query password
        const success = await verifyPassword(queryPassword);
        if (success) {
          // Remove password from URL for security
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("p");
          window.history.replaceState({}, "", newUrl.toString());
          setIsAuthenticated(true);
          setIsChecking(false);
          return;
        }
      }

      // Check if auth is required
      const { data } = await api.GET("/api/auth");
      if (!data) {
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      if (!(data as Record<string, boolean>).authRequired) {
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      setAuthRequired(true);

      // Check if already authenticated in this session
      const storedAuth = sessionStorage.getItem(AUTH_STORAGE_KEY);
      if (storedAuth) {
        // Verify the stored password is still valid
        const success = await verifyPassword(storedAuth);
        if (success) {
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem(AUTH_STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      // If auth check fails, assume no auth required
      setIsAuthenticated(true);
    } finally {
      setIsChecking(false);
    }
  }

  async function verifyPassword(pwd: string): Promise<boolean> {
    try {
      const { data } = await api.POST("/api/auth", {
        body: { password: pwd },
      });

      if ((data as Record<string, boolean> | undefined)?.success) {
        sessionStorage.setItem(AUTH_STORAGE_KEY, pwd);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const success = await verifyPassword(password);

    if (success) {
      setIsAuthenticated(true);
    } else {
      setError("Invalid password");
      setPassword("");
    }

    setIsSubmitting(false);
  }

  // Show loading state while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show password prompt if auth is required and not authenticated
  if (authRequired && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">SaySay</h1>
            <p className="text-muted-foreground">{t.passwordGate.enterPassword}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.passwordGate.placeholder}
              autoFocus
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />

            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={!password || isSubmitting}
              className="w-full h-12 rounded-xl"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Continue"
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Render children if authenticated
  return <>{children}</>;
}
